import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { MutationCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";

/* Das Designsystem zuerst: Schriften, Tokens, Bausteine. Danach erst,
   was diese Anwendung darüber legt — sonst gewinnt die falsche Regel. */
import "@mrmachine/ui";
import "./stile/schale.css";
import "./stile/ladefehler.css";
import "./stile/anmeldung.css";
import "./stile/start.css";
import "./stile/brett.css";
import "./stile/glocke.css";
import "./stile/schnell.css";
import "./stile/vorgaenge.css";
import "./stile/telefon/vorgaenge.css";
import "./stile/telefon/auftrag.css";
import "./stile/telefon/angebot.css";
/* Signal, Paket 4: Liste, Blatt und Akte der Aufträge (docs/64). */
import "./stile/signal-auftraege.css";
import "./stile/kunden.css";
import "./stile/telefon/kunden.css";
import "./stile/deckblatt.css";
import "./stile/stamm.css";
import "./stile/telefon/ressourcen.css";
import "./stile/telefon/preise.css";
import "./stile/telefon/weiteres.css";
import "./stile/info.css";
import "./stile/einstellungen.css";
import "./stile/telefon/einstellungen.css";
/* Die Telefonfassung der geteilten Bausteine zuletzt: Sie zieht nach, was
   die Stile darüber für den Schreibtisch sagen (`stile/telefon.css`). */
import "./stile/telefon.css";

import { SprachRaum } from "./i18n";
import { SitzungsRaum } from "./schale/Sitzung";
import { RufRaum } from "./schale/Ruf";
import { router, wegeNeuPruefen } from "./router";
import { aufRollenwechsel } from "./schale/rechte";
import { aufGeraetwechsel, geraetAnWurzel } from "./schale/geraet";
import { tastaturHorchen } from "./schale/tastatur";
import { standaloneHoeheEinrichten } from "./schale/standaloneHoehe";
import { themaFarbeFolgen } from "./schale/themaFarbe";
import { erscheinungsbildStarten } from "./schale/erscheinungsbild";
import { verlaufFolgen } from "./schale/verlauf";
import { rollleistenFolgen } from "./bausteine/rollleiste";
import { Fehler } from "./daten/klient";
import { nurLesenMelden } from "./daten/nurlesen";
import { warteschlangeStarten } from "./daten/warteschlange";
import { serviceWorkerAnmelden } from "./pwa/anmelden";
import { einmalNeuLaden } from "./schale/Ladefehler";
import { ruheHorchen, schreibquelle, wegGewechselt } from "./schale/ruhe";
import { installhinweisHorchen } from "./pwa/installieren";
/* Signal, Paket 5: die Telefonfassung — **nach** dem Router importiert, damit
   sie auch nach den Stilen der Bausteine kommt, die erst mit ihm geladen
   werden (`handy-leiste.css`, `handy.css`, `blattvonunten.css`). */
/* Signal, Paket 7: was über die übrigen Masken hinweg gilt — vor der
   Telefonfassung, damit die am Telefon das letzte Wort behält. */
import "./stile/signal-masken.css";
import "./stile/signal-telefon.css";

/* Ändert sich die Rolle — die Anmeldung ist durch, der Wiedereinstieg
   fertig, jemand meldet sich ab — oder schaltet jemand auf die große
   Ansicht um, prüft der Router seine Wege noch einmal. Ohne das bliebe
   der Techniker nach dem Anmelden auf der Startseite stehen: Der
   Wegwächter lief, als seine Rolle noch niemand kannte. */
aufRollenwechsel(() => {
  // Eine neue Rolle oder die umgeschaltete große Ansicht ist ein neuer
  // Blick auf denselben Weg — der Wächter fragt wieder nach der Lage.
  wegeNeuPruefen();
  void router.invalidate();
});

/* **Wenn sich das Gerät ändert, prüft der Router nur das Recht**
   (17.09.2026, 40-mobiles-grundgeruest.md, Schritt 3). Wer das Telefon
   dreht, die geteilte Ansicht aufzieht oder das Fenster am Schreibtisch
   schmal macht, hat nichts aufgerufen: Die Adresse bleibt, und die Masken
   zeichnen sich über `useGeraet()` neu. Bis dahin leitete derselbe Anstoß
   um — 1280 → 600 → 1280 px machte aus `/angebote` dauerhaft
   `/handy/angebot`. Gemeldet wird nur der Wechsel der Lage und nicht
   jedes Pixel (`schale/geraet.ts`). Eine Adresse, die nach einem Gerät
   heißt, gibt es seit dem 19.09.2026 nicht mehr (`schale/altadressen.ts`). */
aufGeraetwechsel(() => void router.invalidate());

/* Die Lage steht auch als `data-geraet` an `<html>` — damit die Stile
   dieselbe Antwort lesen wie `useTelefon()` (`schale/geraet.ts`). Vor dem
   ersten Bild, sonst springt jede Maske einmal von der Schreibtisch- in
   die Telefonfassung. */
geraetAnWurzel();
/* Was im Verlauf vor der aktuellen Seite steht — damit der Rückpfeil im
   Kopf zurückgeht, statt einen neuen Eintrag anzulegen (`schale/verlauf.ts`). */
verlaufFolgen(router.history);
/* Und wo die Bildschirmtastatur steht (`schale/tastatur.ts`): Was unten
   klebt, rückt darüber, statt unter ihr zu verschwinden. */
tastaturHorchen();
/* **Die installierte Web-App auf dem iPhone** (07.10.2026, docs/64, Paket 2):
   Nach einem Laden fehlen ihr unter iOS 26 genau die 62 px der Statusleiste
   (`schale/standaloneHoehe.ts`). Außerhalb der Web-App tut es nichts. */
standaloneHoeheEinrichten();
/* Der Browserrahmen (`theme-color`) folgt der gewählten Darstellung, nicht nur
   der des Systems (`schale/themaFarbe.ts`). */
themaFarbeFolgen();
/* Akzentfarbe und Logo des Betriebs (Signal, Paket 6): der gemerkte Stand
   sofort, der Server danach (`schale/erscheinungsbild.ts`). */
erscheinungsbildStarten();
/* Am Telefon rollen Reiter und Filter zur Seite; der gewählte Punkt rückt
   darin ins Bild, wo immer eine Maske ihn setzt (`bausteine/rollleiste.ts`). */
rollleistenFolgen();

/* ══════════════ Wer über „ohne Netz" entscheidet ══════════════
   `networkMode: "always"` — und das ist keine Einstellung, sondern die
   Grundentscheidung aus Migration 0113.

   TanStack Query hält von sich aus **alles an**, sobald `navigator.onLine`
   falsch ist: Abfragen bleiben auf `paused`, und eine Mutation ruft ihre
   Funktion gar nicht erst auf. Am Schreibtisch ist das richtig. Am Telefon
   in der Kiesgrube heißt es: Der Fahrer drückt „Einstempeln", der Knopf sagt
   „Einen Moment …" — und es passiert nie etwas. Genau so stand es am
   16.09.2026 beim ersten Offline-Versuch da.

   Was ohne Netz geschieht, entscheiden hier zwei eigene Stellen: die
   **Mappe** (`daten/mappe.ts`) gibt beim Lesen den letzten Stand zurück, die
   **Warteschlange** (`daten/warteschlange.ts`) nimmt das Geschriebene auf.
   Beide brauchen dafür den Fehlschlag — ein angehaltener Aufruf scheitert
   nicht, er schweigt. */
const abfragen = new QueryClient({
  /* **Ein Schreibversuch am Nur-Lese-Riegel wird gesagt** (P6-01): Die
     Auswahl, die still zurückspringt, war die Antwort auf 403
     `recht.nur_lesen`. Gesagt wird es an einer Stelle (`daten/nurlesen.ts`,
     `schale/Ruf.tsx`), nicht in jeder Maske. */
  mutationCache: new MutationCache({ onError: (fehler) => nurLesenMelden(fehler) }),
  defaultOptions: {
    mutations: { networkMode: "always" },
    queries: {
      networkMode: "always",
      staleTime: 30_000,
      // Ein abgelaufener Kontext wird im Klienten genau einmal nachgeholt.
      // Noch einmal zu versuchen, was schon wiederholt wurde, macht aus
      // einer toten Sitzung einen Sturm auf den Server.
      retry: (anzahl, fehler) =>
        !(fehler instanceof Fehler && [401, 403, 404, 409, 422].includes(fehler.status))
        && anzahl < 2,
      refetchOnWindowFocus: false,
    },
  },
});

/* ══════════════ Das Telefon ══════════════
   Drei Zeilen, und alle drei gehören vor das erste Bild:

   - Der **Service Worker** macht die Anwendung im Funkloch startfähig und
     übernimmt ein Deployment von selbst, im sicheren Moment
     (`pwa/anmelden.ts`, seit dem 19.09.2026 ohne Knopf).
   - Der **Installhinweis** braucht `beforeinstallprompt`, und das Ereignis
     kommt genau einmal — wer es verpasst, bekommt keinen zweiten Anlauf.
   - Die **Warteschlange** schiebt hinaus, was im Funkloch entstanden ist
     (`daten/warteschlange.ts`).

   Danach ist alles alt: Was der Fahrer sieht, entstand vor der Fahrt. Statt
   einzelne Schlüssel aufzuzählen, wird alles für ungültig erklärt — auf
   einem Telefon sind das drei Abfragen, und eine vergessene wäre eine
   Zeile, die nach der Übergabe noch unter RAUS steht. */
/* **Wann ein Neuladen nichts kostet** (`schale/ruhe.ts`, 19.09.2026): Die
   Seite horcht, ob an der Maske gearbeitet wird, und der Router sagt, wann
   die Maske gewechselt hat. Eine Anfrage an den Server, die unterwegs ist,
   zählt als „schreibt noch" — die neue Fassung wartet auf die Antwort. */
ruheHorchen();
schreibquelle(() => abfragen.isMutating() > 0);
router.subscribe("onResolved", ({ toLocation }) => wegGewechselt(toLocation.pathname));

serviceWorkerAnmelden();
/* Vite meldet ein Stück, dessen Stylesheet oder Skript nicht nachkam, über
   ein eigenes Ereignis — dieselbe Lage wie bei Lisa am 02.10.2026 im Funkloch.
   Einmal neu laden statt einer Maske, die halb geladen dasteht. */
window.addEventListener("vite:preloadError", (ereignis) => {
  if (einmalNeuLaden()) ereignis.preventDefault();
});
installhinweisHorchen();
warteschlangeStarten(() => void abfragen.invalidateQueries());

const wurzel = document.getElementById("wurzel");
if (!wurzel) throw new Error("Das Wurzelelement fehlt in index.html.");

createRoot(wurzel).render(
  <StrictMode>
    <QueryClientProvider client={abfragen}>
      <SprachRaum>
        <SitzungsRaum>
          <RufRaum>
            <RouterProvider router={router} />
          </RufRaum>
        </SitzungsRaum>
      </SprachRaum>
    </QueryClientProvider>
  </StrictMode>,
);
